import React, { useContext, Suspense, lazy } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import HomePage from "./pages/HomePage/HomePage";
import AboutPage from "./pages/AboutPage/AboutPage";
import AttendeesPage from "./pages/AttendeesPage/AttendeesPage";
import BlogPage from "./pages/BlogPage/BlogPage";
import FestivalsPage from "./pages/FestivalsPage/FestivalsPage";
import ImportEventPage from "./pages/FestivalsPage/ImportEventPage";
import EditEventPage from "./pages/FestivalsPage/EditEventPage";
import EventDetailPage from "./pages/EventDetailPage/EventDetailPage";
import MyAccountPage from "./pages/MyAccountPage/MyAccountPage";
import LoginForm from "./pages/LoginForm/LoginForm";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail/VerifyEmail";
import { ProtectedRoute } from "./ProtectedRoutes/ProtectedRoute";
import { UserContext } from "./context/UserContext";
import ProfileDashboard from "./pages/ProfileDashboard/ProfileDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService/TermsOfService";
import Notification from "./pages/NotificationPage/Notification";
import WriteArticlePage from "./pages/WriteArticlePage/WriteArticlePage";
import CataloguePage from "./pages/CataloguePage/CataloguePage";
import ReviewsPage from "./pages/ReviewsPage/Reviews";
import DanceGoodsPage from "./pages/DanceGoodsPage/DanceGoodsPage";

// Lazy load admin module for code splitting
const AdminModule = lazy(() => import("./admin"));

// Redirect component for old URLs to unified pages
const PastEventRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/event/${id}`} replace />;
};

const EventsIdRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/event/${id}`} replace />;
};

// Loading fallback for lazy-loaded admin module
const AdminLoadingFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #6B3A5B 0%, #4a2840 100%)',
    color: 'white'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(255,255,255,0.3)',
        borderTopColor: 'white',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 1rem'
      }} />
      <p>Loading Admin Dashboard...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
);

const Router = () => {
  const { user } = useContext(UserContext);

  const RedirectToHomeIfLoggedIn = ({ children }) => {
    return user ? (
      <Navigate to="/" replace />
    ) : (
      children
    );
  };
  return (
    <>
      <Routes>
        {/* ===== ACTIVE ROUTES ===== */}
        <Route path="/" exact element={<HomePage />} />
        <Route path="/events" element={<FestivalsPage />} />
        <Route path="/events/import" element={<ImportEventPage />} />
        <Route
          path="/events/edit/:id"
          element={
            <ProtectedRoute user={user}>
              <EditEventPage />
            </ProtectedRoute>
          }
        />
        <Route path="/about" element={<AboutPage />} />
        <Route
          path="/event/:eventId/attendees"
          element={
            <ProtectedRoute user={user}>
              <AttendeesPage />
            </ProtectedRoute>
          }
        />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:articleId" element={<BlogPage />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/goods" element={<DanceGoodsPage />} />
        <Route path="/notification" element={<Notification />} />
        <Route path="/event/:id" element={<EventDetailPage />} />
        <Route path="/myaccount" element={<MyAccountPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route
          path="/catalogue"
          element={
            <ProtectedRoute user={user}>
              <CataloguePage />
            </ProtectedRoute>
          }
        />
        {/* TopicCardReadmore deprecated - redirect to blog */}
        <Route path="/topicCardreadmore/:id" element={<Navigate to="/blog" replace />} />

        {/* ===== DEPRECATED ROUTE REDIRECTS ===== */}
        {/* Redirect old /festivals to /events */}
        <Route path="/festivals" element={<Navigate to="/events" replace />} />
        {/* Redirect old past-event URLs to unified event page */}
        <Route path="/past-event/:id" element={<PastEventRedirect />} />
        {/* Redirect old /blogs to /blog */}
        <Route path="/blogs" element={<Navigate to="/blog" replace />} />
        {/* Redirect old /myprofile to /profile */}
        <Route path="/myprofile" element={<Navigate to="/profile" replace />} />
        {/* Redirect old /favoriteevents to profile events section */}
        <Route path="/favoriteevents" element={<Navigate to="/profile?section=events-favorites" replace />} />
        {/* Redirect old profile route */}
        <Route path="/profile-old" element={<Navigate to="/profile" replace />} />
        {/* Redirect other deprecated routes to home */}
        <Route path="/eventDetail" element={<Navigate to="/events" replace />} />
        <Route path="/blog-discover" element={<Navigate to="/blog" replace />} />
        <Route path="/recent-kudos" element={<Navigate to="/profile?section=kudos-received" replace />} />
        <Route path="/alltopics" element={<Navigate to="/blog" replace />} />
        <Route path="/articles" element={<Navigate to="/blog" replace />} />
        <Route path="/m&m/catalogue" element={<Navigate to="/blog" replace />} />
        <Route path="/events-card" element={<Navigate to="/events" replace />} />
        <Route path="/hpcelebrate" element={<Navigate to="/" replace />} />
        <Route path="/hpshare" element={<Navigate to="/" replace />} />
        <Route path="/hpmovemuse" element={<Navigate to="/" replace />} />
        <Route path="/discoverevent" element={<Navigate to="/events" replace />} />
        <Route path="/blogreadmoreinprofile" element={<Navigate to="/blog" replace />} />

        <Route
          path="/write"
          element={
            <ProtectedRoute user={user}>
              <WriteArticlePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute user={user}>
              <ProfileDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/login"
          element={
            <RedirectToHomeIfLoggedIn>
              <LoginForm />
            </RedirectToHomeIfLoggedIn>
          }
        />
        <Route path='/signup' element={<Navigate to="/login?tab=signup" replace />} />
        <Route path='/reset-password' element={<ResetPassword/>} />
        <Route path='/verify-email' element={<VerifyEmail/>} />

        {/* Admin Module - Lazy loaded */}
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<AdminLoadingFallback />}>
              <AdminModule />
            </Suspense>
          }
        />
      </Routes>
    </>
  );
};

export default Router;
